import type { Page } from '../../app/feature';
import { delegate, esc, q } from '../../shared/dom';
import { t } from '../../shared/i18n';
import { fmt } from '../../shared/format';
import { ApiError } from '../../shared/api/http';
import { getState } from '../../shared/store/store';
import { hydrateFromApi } from '../../shared/store/sync';
import { toast } from '../../shared/ui/toast';
import { href, navigate } from '../../app/router';
import { missionsApi } from './missions.api';
import { missionIcon, missionLabel, missionMeasure, missionsSubnav, ruleSentence } from './missions';

/**
 * Misiones configuradas: a lista do que está valendo, com pausar/retomar, editar e excluir.
 * Subitem de Misiones no menu; a criação/edição fica na página `/misiones`.
 */
export const missionsList: Page = {
  id: 'misiones-lista',
  pattern: '/misiones/configuradas',
  roles: ['ADMIN'],
  nav: { key: 'nav.missionsList', icon: '📋', group: 'config', parent: 'misiones' },

  template: () => `
<div>
  <div class="view-head">
    <div>
      <h2 data-i18n="mis.listTitle">Misiones configuradas</h2>
      <p data-i18n="mis.listSub">Todo lo que hoy genera números de la suerte. Pausar una misión deja de generar números sin borrar el historial; editar vale para lo que pase de ahí en adelante.</p>
    </div>
    <div class="spacer"></div>
    <a class="btn btn-primary" href="${href('/misiones')}">+ <span data-i18n="mis.new">Nueva misión</span></a>
  </div>
  ${missionsSubnav('list')}

  <div class="cols" style="margin-bottom:18px">
    <div class="kpi span-4" style="--accent:var(--green)"><div class="k-label" data-i18n="mis.kActive">Misiones activas</div><div class="k-val tnum" id="ml-active">0</div><div class="k-sub" data-i18n="mis.kActiveSub">generando números ahora</div></div>
    <div class="kpi span-4" style="--accent:var(--line-2)"><div class="k-label" data-i18n="mis.kPaused">En pausa</div><div class="k-val tnum" id="ml-paused">0</div><div class="k-sub" data-i18n="mis.kPausedSub">historial conservado</div></div>
    <div class="kpi span-4" style="--accent:var(--gold)"><div class="k-label" data-i18n="mis.kIssued">Números emitidos</div><div class="k-val tnum" id="ml-issued">0</div><div class="k-sub" data-i18n="mis.kIssuedSub">por todas las misiones</div></div>
  </div>

  <div class="tw">
    <table id="tbl-missions">
      <thead><tr>
        <th data-i18n="mis.eventCol">Evento</th>
        <th data-i18n="mis.ruleCol">Regla</th>
        <th data-i18n="th.issued">Números emitidos</th>
        <th data-i18n="th.status">Estado</th>
        <th></th>
      </tr></thead>
      <tbody></tbody>
    </table>
  </div>
</div>`,

  mount() {
    const table = q('#tbl-missions');
    delegate(table, 'click', '[data-edit]', el => navigate(`/misiones?edit=${el.dataset.edit}`));
    delegate(table, 'click', '[data-toggle]', async el => {
      const m = getState().missions.find(x => x.id === el.dataset.toggle); if (!m) return;
      await run(() => missionsApi.update(m.id, { active: !m.active }));
    });
    delegate(table, 'click', '[data-del]', async el => {
      const m = getState().missions.find(x => x.id === el.dataset.del); if (!m) return;
      if (!confirm(t('mis.confirmDelete').replace('{name}', missionLabel(m)))) return;
      await run(() => missionsApi.remove(m.id));
    });
  },

  render() {
    const ms = getState().missions;
    q('#ml-active').textContent = fmt(ms.filter(m => m.active).length);
    q('#ml-paused').textContent = fmt(ms.filter(m => !m.active).length);
    q('#ml-issued').textContent = fmt(ms.reduce((s, m) => s + m.issuedCount, 0));

    q('#tbl-missions tbody').innerHTML = ms.map(m => `<tr class="${m.active ? '' : 'muted'}">
      <td><div class="mis-ev"><i>${missionIcon(m)}</i><div><b>${esc(missionLabel(m))}</b><span>${esc(missionMeasure(m))}</span></div></div></td>
      <td style="font-size:13px; line-height:1.45; max-width:420px">${esc(ruleSentence(m))}${m.descriptionEs ? `<div class="muted" style="font-size:11.5px; margin-top:3px">“${esc(m.descriptionEs)}”</div>` : ''}</td>
      <td class="tnum">${fmt(m.issuedCount)}</td>
      <td><span class="chip ${m.active ? 'chip-green' : 'chip-dim'}">${m.active ? t('mis.active') : t('mis.paused')}</span></td>
      <td style="text-align:right; white-space:nowrap">
        <button class="btn btn-sm" data-edit="${m.id}">${esc(t('common.edit'))}</button>
        <button class="btn btn-sm btn-ghost" data-toggle="${m.id}">${m.active ? esc(t('mis.deactivate')) : esc(t('mis.activate'))}</button>
        ${m.issuedCount === 0 ? `<button class="btn btn-sm btn-ghost" data-del="${m.id}" title="${esc(t('common.del'))}">✕</button>` : ''}
      </td>
    </tr>`).join('') || `<tr><td colspan="5" class="empty">${esc(t('mis.none'))} <a class="auth-link" href="${href('/misiones')}">${esc(t('mis.new'))}</a></td></tr>`;
  }
};

async function run(action: () => Promise<unknown>): Promise<void> {
  try { await action(); toast(t('msg.saved')); await hydrateFromApi(); }
  catch (err) { toast(err instanceof ApiError ? err.message : t('auth.errGeneric')); }
}
